import React from 'react'
import moment from 'moment'

/**
 * Custom hook for managing date range picker state and handlers
 * Supports single date selection (from/to) and range selection
 */
export const useDateRangePicker = (initialDateBegin, initialDateEnd) => {
  const [state, setState] = React.useState({
    dateBegin: initialDateBegin || moment().startOf('day').subtract(10, 'days').add(1, 'days'),
    dateEnd: initialDateEnd || moment().endOf('day'),
    dateType: undefined,
    dayPickerIsVisible: false,
    draftRange: null,
  })

  const getInclusiveDays = React.useCallback((from, to) => {
    if (!from || !to) return 0
    const start = moment(from).startOf('day')
    const end = moment(to).startOf('day')
    return end.diff(start, 'days') + 1
  }, [])

  const closeDayPicker = React.useCallback(() => {
    setState(prev => ({
      ...prev,
      dayPickerIsVisible: false,
      dateType: undefined,
      draftRange: null,
    }))
  }, [])

  const editDateRange = React.useCallback(() => {
    setState(prev => ({
      ...prev,
      dayPickerIsVisible: !prev.dayPickerIsVisible,
      dateType: undefined,
      draftRange: !prev.dayPickerIsVisible
        ? {
          from: prev.dateBegin ? prev.dateBegin.toDate() : moment().startOf('day').toDate(),
          to: prev.dateEnd ? prev.dateEnd.toDate() : moment().endOf('day').toDate(),
        }
        : null,
    }))
  }, [])

  const editDateFrom = React.useCallback(() => {
    setState(prev => ({
      ...prev,
      dayPickerIsVisible: true,
      dateType: 'from',
      draftRange: {
        from: prev.dateBegin ? prev.dateBegin.toDate() : moment().startOf('day').toDate(),
        to: prev.dateEnd ? prev.dateEnd.toDate() : moment().endOf('day').toDate(),
      },
    }))
  }, [])

  const editDateTo = React.useCallback(() => {
    setState(prev => ({
      ...prev,
      dayPickerIsVisible: true,
      dateType: 'to',
      draftRange: {
        from: prev.dateBegin ? prev.dateBegin.toDate() : moment().startOf('day').toDate(),
        to: prev.dateEnd ? prev.dateEnd.toDate() : moment().endOf('day').toDate(),
      },
    }))
  }, [])

  const handleDayClick = React.useCallback((range) => {
    if (!range) {
      // Range cleared, reset selection
      setState(prev => ({
        ...prev,
        draftRange: null,
      }))
      return
    }

    setState((prevState) => {
      const { draftRange, dateBegin, dateEnd } = prevState

      // Default range mode behavior for a single DayPicker in range mode
      let from
      let to

      if (range instanceof Date) {
        from = range
        to = draftRange?.to
      } else if (range.from) {
        from = range.from
        to = range.to
      } else {
        return prevState
      }

      const today = moment().endOf('day')
      const validatedFrom = from ? moment(from).startOf('day') : null
      let validatedTo = to ? moment(to).endOf('day') : null

      if (validatedFrom && validatedFrom.isAfter(today, 'day')) return prevState
      if (validatedTo && validatedTo.isAfter(today, 'day')) {
        validatedTo = today
      }

      if (validatedFrom && validatedTo && validatedTo.isBefore(validatedFrom, 'day')) {
        validatedTo = validatedFrom.clone().endOf('day')
      }

      if (validatedFrom && validatedTo) {
        const inclusiveDays = getInclusiveDays(validatedFrom, validatedTo)
        if (inclusiveDays > 31) {
          // Enforce maximum 31 calendar days inclusive
          validatedTo = validatedFrom.clone().add(30, 'days').endOf('day')
        }
      }

      return {
        ...prevState,
        draftRange: {
          from: validatedFrom ? validatedFrom.toDate() : null,
          to: validatedTo ? validatedTo.toDate() : null,
        },
      }
    })
  }, [])

  const applyDraftRange = React.useCallback(() => {
    setState(prev => {
      const { draftRange } = prev
      if (!draftRange?.from || !draftRange?.to) return prev

      const from = moment(draftRange.from).startOf('day')
      let to = moment(draftRange.to).endOf('day')
      const today = moment().endOf('day')

      if (from.isAfter(today, 'day')) return prev
      if (to.isAfter(today, 'day')) {
        to = today
      }
      if (to.isBefore(from, 'day')) {
        to = from.clone().endOf('day')
      }
      const inclusiveDays = getInclusiveDays(from, to)
      if (inclusiveDays > 31) {
        // Enforce maximum 31 calendar days inclusive
        to = from.clone().add(30, 'days').endOf('day')
      }

      return {
        ...prev,
        dateBegin: from,
        dateEnd: to,
        dayPickerIsVisible: false,
        dateType: undefined,
        draftRange: null,
      }
    })
  }, [])

  const resetToLast31Days = React.useCallback(() => {
    const dateBegin = moment()
      .startOf('day')
      .subtract(30, 'days') // 30 days ago to today = 31 days total

    const dateEnd = moment()
      .endOf('day')

    setState(prev => ({
      ...prev,
      dateBegin,
      dateEnd,
      dayPickerIsVisible: false,
      dateType: undefined,
      draftRange: null,
    }))
  }, [])

  const setDateRange = React.useCallback((dateBegin, dateEnd) => {
    setState(prev => ({
      ...prev,
      dateBegin,
      dateEnd,
    }))
  }, [])

  return {
    state,
    setState,
    closeDayPicker,
    editDateRange,
    editDateFrom,
    editDateTo,
    applyDraftRange,
    handleDayClick,
    resetToLast31Days,
    setDateRange,
  }
}

